<!--
  AdminMetrics.svelte — the ad-hoc metric explorer.

  Pick one or more metrics, filter and split the data, then chart it over a date
  range. The whole UI is built from the server's catalogue rather than a hard-coded
  metric list, so a metric added to the registry appears here with no client change.

  A group-by or filter field is only offered when *every* selected metric declares
  it: the query applies the split to all of them, and the engine rejects a field a
  metric does not expose rather than quietly returning it unsplit.
-->
<script lang="ts">
	import { onDestroy, onMount, tick } from 'svelte';
	import ApexCharts from 'apexcharts';
	import {
		Button,
		Checkbox,
		Helper,
		Input,
		Label,
		Select,
		Spinner,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { EmptyState, PageHeader, RoutedLink } from '../../components';
	import MetricPicker from './analytics/MetricPicker.svelte';
	import { t, t2 } from '../../i18n';
	import { getAnalyticsCatalog, getAnalyticsFieldValues, runAnalyticsQuery } from '../../AppNetwork';
	import type {
		AnalyticsCatalog,
		AnalyticsField,
		AnalyticsFieldValue,
		AnalyticsFilterClause,
		AnalyticsMetric,
		AnalyticsSeries
	} from '../../AppTypes';
	import {
		buildChartOptions,
		downloadFile,
		formatValue,
		groupSegments,
		seriesToCsv
	} from '../../analytics/metricCharts';
	import { toDateInputValue } from '../../analytics/dashboardQueries';

	/** Operators that take no value. */
	const VALUELESS_OPERATORS = new Set(['isNull', 'notNull']);

	/** The metric catalogue, once loaded. */
	let catalog: AnalyticsCatalog | undefined = $state();
	/** Whether the catalogue is still loading. */
	let loadingCatalog = $state(true);
	/** Why the catalogue could not be loaded, if it could not. */
	let catalogError = $state('');

	/** Which metrics are selected, keyed by metric key. */
	let selectedMetrics: Record<string, boolean> = $state({});
	/** The bucket granularity. */
	let granularity = $state('day');
	/*
	 * Default to the last 90 days so a first query is bounded, and its axis predictable,
	 * rather than scanning all of history. Clearing the dates gives an all-time query.
	 */
	let fromDate = $state(toDateInputValue(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)));
	/** The exclusive upper bound of the range. */
	let toDate = $state(toDateInputValue(new Date()));
	/** Which dimensions the result is split by, keyed by field key. */
	let groupBy: Record<string, boolean> = $state({});
	/** The filter clauses. */
	let filters: AnalyticsFilterClause[] = $state([]);
	/** The cap on distinct groups. */
	let maxGroups = $state(50);
	/** The row limit. */
	let limit = $state(100);

	/** The last result series. */
	let series: AnalyticsSeries[] = $state([]);
	/** Whether a query is in flight. */
	let running = $state(false);
	/** Why the last query failed, if it did. */
	let runError = $state('');
	/** Whether a query has been run at all. */
	let hasRun = $state(false);

	/** Distinct values per field key, populating the autocomplete lists. */
	let filterValues: Record<string, AnalyticsFieldValue[]> = $state({});
	/** Debounce timers for the value typeahead, per field key. */
	const valueSearchTimers: Record<string, ReturnType<typeof setTimeout>> = {};

	/** The chart container. */
	let chartElement: HTMLDivElement | undefined = $state();
	/** The live chart, if any. */
	let chart: ApexCharts | undefined;

	/** The metric keys currently selected. */
	let selectedMetricKeys = $derived(Object.keys(selectedMetrics).filter((key) => selectedMetrics[key]));

	/**
	 * The fields every selected metric declares.
	 *
	 * The intersection, not the union: the query applies a split or filter to all of the
	 * selected metrics at once, and the engine rejects a field one of them does not expose.
	 */
	let availableFields: AnalyticsField[] = $derived.by(() => {
		const loaded = catalog;
		/* Nothing selected, or nothing loaded, offers no dimensions to split by. */
		if (loaded == null || selectedMetricKeys.length === 0) return [];

		const metrics = selectedMetricKeys
			.map((key) => loaded.Metrics.find((metric) => metric.Key === key))
			.filter((metric): metric is AnalyticsMetric => metric != null);
		/* A selection the catalogue no longer knows offers no dimensions. */
		if (metrics.length === 0) return [];

		let commonKeys = new Set(metrics[0].Fields.map((field) => field.Key));
		/* Narrow to the fields every remaining metric also declares. */
		for (const metric of metrics.slice(1)) {
			const keys = new Set(metric.Fields.map((field) => field.Key));
			commonKeys = new Set([...commonKeys].filter((key) => keys.has(key)));
		}
		return metrics[0].Fields.filter((field) => commonKeys.has(field.Key));
	});

	/** Whether at least one selected metric supports a time axis. */
	let hasTimeSeries = $derived.by(() => {
		const loaded = catalog;
		return (
			loaded != null &&
			selectedMetricKeys.some((key) => loaded.Metrics.find((metric) => metric.Key === key)?.TimeSeries === true)
		);
	});

	/**
	 * Drop any split or filter the current metric selection no longer supports.
	 *
	 * Changing the selected metrics can shrink the intersection above. Left alone, the
	 * query would reference a field one metric does not declare and the engine would
	 * reject the whole thing — with no visible cause, since the offending control is
	 * no longer on screen.
	 */
	$effect(() => {
		const validKeys = new Set(availableFields.map((field) => field.Key));

		const prunedGroupBy: Record<string, boolean> = {};
		/* Keep only the splits the narrowed field set still supports. */
		for (const key of Object.keys(groupBy)) {
			if (validKeys.has(key)) prunedGroupBy[key] = groupBy[key];
		}
		/* Reassign only on a real change, so the effect does not loop on its own write. */
		if (Object.keys(prunedGroupBy).length !== Object.keys(groupBy).length) groupBy = prunedGroupBy;

		const prunedFilters = filters.filter((filter) => validKeys.has(filter.Field));
		/* Same guard as above: only write back when something actually went. */
		if (prunedFilters.length !== filters.length) filters = prunedFilters;
	});

	/**
	 * The field definitions of one metric, which say which grouped values link to an object.
	 *
	 * @param metricKey - The metric to describe.
	 * @returns Its fields, or an empty list when it is not in the catalogue.
	 */
	function fieldsOfMetric(metricKey: string): AnalyticsField[] {
		return catalog?.Metrics.find((metric) => metric.Key === metricKey)?.Fields ?? [];
	}

	onMount(async () => {
		try {
			catalog = await getAnalyticsCatalog();
			maxGroups = catalog.DefaultMaxGroups ?? maxGroups;
		} catch (caught) {
			catalogError = caught instanceof Error ? caught.message : String(caught);
		} finally {
			loadingCatalog = false;
		}
	});

	onDestroy(() => {
		chart?.destroy();
		for (const timer of Object.values(valueSearchTimers)) clearTimeout(timer);
	});

	/** Clears every selected metric. */
	function fnClearMetrics(): void {
		selectedMetrics = {};
	}

	/**
	 * Toggles one group-by dimension.
	 *
	 * @param key - The field key to split by, or stop splitting by.
	 */
	function fnToggleGroupBy(key: string): void {
		groupBy = { ...groupBy, [key]: groupBy[key] !== true };
	}

	/** Adds an empty filter clause on the first available field. */
	function fnAddFilter(): void {
		const field = availableFields[0]?.Key ?? '';
		filters = [...filters, { Field: field, Operator: 'eq', Value: '' }];
		fnLoadFieldValues(field);
	}

	/**
	 * Removes one filter clause.
	 *
	 * @param index - The clause to remove.
	 */
	function fnRemoveFilter(index: number): void {
		filters = filters.filter((_unused, position) => position !== index);
	}

	/**
	 * Loads the distinct values of a field for the autocomplete list.
	 *
	 * @param fieldKey - The field to enumerate.
	 * @param search   - What has been typed, to narrow the list server-side.
	 */
	async function fnLoadFieldValues(fieldKey: string, search = ''): Promise<void> {
		if (fieldKey == null || fieldKey.length === 0) return;
		/* Autocomplete from whichever selected metric declares the field. */
		const metricKey = selectedMetricKeys.find((key) =>
			catalog?.Metrics.find((metric) => metric.Key === key)?.Fields.some((field) => field.Key === fieldKey)
		);
		if (metricKey == null) return;
		filterValues = { ...filterValues, [fieldKey]: await getAnalyticsFieldValues(metricKey, fieldKey, search) };
	}

	/**
	 * Drives the debounced server-side typeahead as a filter value is typed.
	 *
	 * @param fieldKey - The field being filtered.
	 * @param event    - The input event.
	 */
	function fnOnFilterValueInput(fieldKey: string, event: Event): void {
		const search = (event.target as HTMLInputElement)?.value ?? '';
		clearTimeout(valueSearchTimers[fieldKey]);
		valueSearchTimers[fieldKey] = setTimeout(() => fnLoadFieldValues(fieldKey, search), 250);
	}

	/** Runs the query the controls describe. */
	async function fnRun(): Promise<void> {
		if (selectedMetricKeys.length === 0) {
			runError = t2('Select at least one metric.');
			return;
		}

		running = true;
		runError = '';

		/* Normalize: drop empty clauses, and turn an `in` list into the array the engine needs. */
		const normalizedFilters: AnalyticsFilterClause[] = filters
			.filter((filter) => filter.Field != null && filter.Field.length > 0)
			.map((filter) => {
				if (VALUELESS_OPERATORS.has(filter.Operator)) return { Field: filter.Field, Operator: filter.Operator };
				/* `in` is typed as a comma-separated list but the engine requires an array. */
				if (filter.Operator === 'in') {
					const values = String(filter.Value ?? '')
						.split(',')
						.map((part) => part.trim())
						.filter((part) => part.length > 0);
					return { Field: filter.Field, Operator: filter.Operator, Value: values };
				}
				return filter;
			});

		/* A rejected query is reported beside the Run button rather than as a toast. */
		try {
			const response = await runAnalyticsQuery({
				Metrics: selectedMetricKeys,
				From: fromDate.length > 0 ? fromDate : undefined,
				To: toDate.length > 0 ? toDate : undefined,
				Granularity: granularity,
				GroupBy: Object.keys(groupBy).filter((key) => groupBy[key]),
				Filters: normalizedFilters,
				MaxGroups: maxGroups,
				Limit: limit
			});
			series = response.Series ?? [];
			hasRun = true;
			/* Wait for the chart container to be laid out before ApexCharts measures it. */
			await tick();
			fnRenderChart();
		} catch (caught) {
			runError = caught instanceof Error ? caught.message : String(caught);
		} finally {
			running = false;
		}
	}

	/** Renders the result chart, replacing whatever was there. */
	function fnRenderChart(): void {
		const options = buildChartOptions(series, {
			granularity,
			fromDate,
			toDate,
			metricFill: (key) => catalog?.Metrics.find((metric) => metric.Key === key)?.Fill ?? 'zero',
			valueLabel: t2('Value')
		});
		chart?.destroy();
		chart = undefined;
		/* Nothing to draw, or nowhere to draw it. */
		if (options == null || chartElement == null) return;
		chart = new ApexCharts(chartElement, options);
		chart.render();
	}

	/** Downloads the result series as CSV. */
	function fnExportCsv(): void {
		downloadFile('metrics.csv', seriesToCsv(series), 'text/csv');
	}
</script>

<PageHeader
	title={$t('Metrics')}
	description={$t(
		'Build custom analytics across accounts, authentication, applications and reliability. Pick one or more metrics, filter and split the data, then chart it over a date range.'
	)}
/>

{#if loadingCatalog}
	<EmptyState variant="loading" title={$t('Loading metrics…')} />
{:else if catalogError.length > 0}
	<EmptyState variant="error" title={$t('Could not load the metric catalogue.')} description={catalogError} />
{:else if catalog != null}
	<div class="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
		<AbstractCard>
			<div class="mb-2 flex items-center">
				<h5 class="widget-title">{$t('Metrics')}</h5>
				<span class="grow"></span>
				<span class="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
					{t2('{{count}} selected', { count: selectedMetricKeys.length })}
				</span>
				{#if selectedMetricKeys.length > 0}
					<Button size="xs" color="light" class="ml-2" onclick={fnClearMetrics}>{$t('Clear')}</Button>
				{/if}
			</div>
			<MetricPicker {catalog} multiple bind:selected={selectedMetrics} scrollable />
		</AbstractCard>

		<AbstractCard>
			<h5 class="widget-title mb-2">{$t('Time range')}</h5>
			<Label class="mb-1" for="metrics-from">{$t('From')}</Label>
			<Input id="metrics-from" type="date" bind:value={fromDate} class="mb-2" />
			<Label class="mb-1" for="metrics-to">{$t('To')}</Label>
			<Input id="metrics-to" type="date" bind:value={toDate} class="mb-2" />
			<Label class="mb-1" for="metrics-granularity">{$t('Granularity')}</Label>
			<Select id="metrics-granularity" bind:value={granularity} disabled={!hasTimeSeries}>
				{#each catalog.Granularities as option (option)}
					<option value={option}>{$t(option)}</option>
				{/each}
			</Select>
			{#if !hasTimeSeries}
				<Helper class="mt-1">{$t('The selected metrics are snapshots and ignore the time range.')}</Helper>
			{/if}
			<Label class="mb-1 mt-2" for="metrics-limit">{$t('Row limit')}</Label>
			<Input id="metrics-limit" type="number" bind:value={limit} min="1" />
		</AbstractCard>

		<AbstractCard>
			<h5 class="widget-title mb-2">{$t('Group by')}</h5>
			{#if availableFields.length === 0}
				<Helper>{$t('Select metrics to see the dimensions they can be split by.')}</Helper>
			{:else}
				<div class="space-y-1">
					{#each availableFields as field (field.Key)}
						<!--
							One-way `checked` plus a handler, never `bind:checked`: a field that has
							never been toggled is absent from the record, and Svelte 5 throws
							`props_invalid_value` on binding `undefined` to a prop whose fallback is
							`false` — which flowbite's Checkbox declares. The throw aborts the whole
							reactive pass, so the symptom is unrelated controls silently freezing.
						-->
						<Checkbox
							checked={groupBy[field.Key] === true}
							onchange={() => fnToggleGroupBy(field.Key)}
						>
							<span class="ml-1">{$t(field.Label)}</span>
						</Checkbox>
					{/each}
				</div>
				<Label class="mb-1 mt-3" for="metrics-maxgroups">{$t('Max groups')}</Label>
				<Input id="metrics-maxgroups" type="number" bind:value={maxGroups} min="1" max={catalog.MaxGroups} />
				<Helper class="mt-1">
					{$t(
						'Only the top groups by total value are returned, so a high-cardinality dimension stays fast.'
					)}
				</Helper>
			{/if}
		</AbstractCard>
	</div>

	<AbstractCard class="mt-4">
		<div class="mb-2 flex items-center">
			<h5 class="widget-title">{$t('Filters')}</h5>
			<span class="grow"></span>
			<Button size="xs" color="light" onclick={fnAddFilter} disabled={availableFields.length === 0}>
				<i class="fa-solid fa-plus mr-1 text-xs"></i>{$t('Add filter')}
			</Button>
		</div>
		{#if filters.length === 0}
			<Helper>{$t('No filters. The query runs across all data in range.')}</Helper>
		{/if}
		{#each filters as filter, index (index)}
			<div class="mb-2 flex flex-wrap items-center gap-2">
				<Select class="!w-48" bind:value={filter.Field} onchange={() => fnLoadFieldValues(filter.Field)}>
					{#each availableFields as field (field.Key)}
						<option value={field.Key}>{$t(field.Label)}</option>
					{/each}
				</Select>
				<Select class="!w-40" bind:value={filter.Operator}>
					{#each catalog.Operators as operator (operator)}
						<option value={operator}>{operator}</option>
					{/each}
				</Select>
				{#if !VALUELESS_OPERATORS.has(filter.Operator)}
					<Input
						class="!w-56"
						bind:value={filter.Value as string}
						list={`fv-${filter.Field}`}
						oninput={(event) => fnOnFilterValueInput(filter.Field, event)}
						placeholder={filter.Operator === 'in' ? $t('Comma-separated values') : $t('Value')}
					/>
				{/if}
				<Button size="xs" color="red" onclick={() => fnRemoveFilter(index)} aria-label={$t('Remove filter')}>
					<i class="fa-solid fa-trash text-xs"></i>
				</Button>
			</div>
		{/each}

		<!-- The values that actually occur for each filtered field. -->
		{#each Object.entries(filterValues) as [fieldKey, values] (fieldKey)}
			<datalist id={`fv-${fieldKey}`}>
				{#each values as suggestion (suggestion.Value)}
					<option value={suggestion.Value}>{suggestion.Label ?? suggestion.Value}</option>
				{/each}
			</datalist>
		{/each}
	</AbstractCard>

	<div class="mt-4 flex items-center gap-3">
		<Button onclick={fnRun} disabled={running || selectedMetricKeys.length === 0}>
			{#if running}
				<Spinner size="4" class="mr-2" />
			{:else}
				<i class="fa-solid fa-play mr-2 text-xs"></i>
			{/if}
			{$t('Run query')}
		</Button>
		{#if series.length > 0}
			<Button size="sm" color="light" onclick={fnExportCsv}>
				<i class="fa-solid fa-file-csv mr-1 text-xs"></i>{$t('Export CSV')}
			</Button>
		{/if}
		{#if runError.length > 0}
			<span class="text-red-600 dark:text-red-400">{runError}</span>
		{/if}
	</div>

	<AbstractCard class="mt-4">
		<div bind:this={chartElement} class={series.length === 0 ? 'hidden' : ''}></div>
		{#if series.length === 0}
			<p class="py-8 text-center text-gray-500">
				{hasRun ? $t('That query returned no data.') : $t('Run a query to see results.')}
			</p>
		{/if}
	</AbstractCard>

	{#if series.length > 0}
		<AbstractCard class="mt-4">
			<h5 class="widget-title mb-2">{$t('Data')}</h5>
			<Table divClass="widget !p-1 relative" class="dataTable">
				<TableHead>
					<TableHeadCell>{$t('Metric')}</TableHeadCell>
					<TableHeadCell>{$t('Bucket')}</TableHeadCell>
					<TableHeadCell>{$t('Group')}</TableHeadCell>
					<TableHeadCell>{$t('Value')}</TableHeadCell>
				</TableHead>
				<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
					{#each series as entry (entry.Metric)}
						{#each entry.Points as point, index (index)}
							<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
								<TableBodyCell>{$t(entry.Label)}</TableBodyCell>
								<TableBodyCell>{point.Bucket ?? '—'}</TableBodyCell>
								<TableBodyCell>
									{#each groupSegments(point, fieldsOfMetric(entry.Metric)) as segment, position (segment.key)}
										{#if position > 0}<span class="text-gray-400"> / </span>{/if}
										{#if segment.href != null}
											<RoutedLink to={segment.href} class="text-primary-600 hover:underline"
												>{segment.label}</RoutedLink
											>
										{:else}
											{segment.label}
										{/if}
									{:else}
										—
									{/each}
								</TableBodyCell>
								<TableBodyCell>{formatValue(point.Value, entry.Unit)}</TableBodyCell>
							</TableBodyRow>
						{/each}
					{/each}
				</TableBody>
			</Table>
		</AbstractCard>
	{/if}
{/if}
